/*
 * Author: Ben Zhao
 * File Created: Thursday, 24-12-19 05:45 pm
 * Last Modified: Friday, 25-06-06 01:56 pm, by Ben Zhao
 */

import CryptoJS, { algo, enc, HmacSHA256 } from 'crypto-js';
import Forge from 'node-forge';

export const createHmacSHA256 = (key: string, data: string) => {
  return algo.HMAC.create(algo.SHA256, key)
    .update(data)
    .finalize()
    .toString(enc.Hex);
};

/** 生成随机密钥，默认16字节(128位)的 */
export function generateRandomKey(length = 16) {
  // if (typeof window === 'undefined') {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return btoa(result);
}

// 混合加密方案：RSA+AES
function hybridEncrypt(publicKey: string, message: string) {
  // 1. 生成随机AES密钥(128位)
  const aesKey = CryptoJS.lib.WordArray.random(16).toString(
    CryptoJS.enc.Base64
  );

  // 2. 使用AES-CBC加密消息
  const iv = CryptoJS.lib.WordArray.random(16); // 初始化向量
  const encrypted = CryptoJS.AES.encrypt(
    message,
    CryptoJS.enc.Base64.parse(aesKey),
    {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  );

  // 3. 使用RSA加密AES密钥
  const pk = Forge.pki.publicKeyFromPem(
    `-----BEGIN PUBLIC KEY-----\n${publicKey}\n-----END PUBLIC KEY-----`
  );

  // UTF-8编码数据
  const bytes = Forge.util.encodeUtf8(aesKey);

  // 执行加密操作 (RSAES-PKCS1-V1_5 填充模式)
  const encryptedAesKey = Forge.util.encode64(pk.encrypt(bytes));

  // 4. 返回所有需要的信息
  return {
    encryptedData: encrypted.toString(),
    encryptedAesKey: encryptedAesKey,
    iv: iv.toString(CryptoJS.enc.Base64),
  };
}

/**
 * sha256WithSalt - 使用SHA-256算法对字符串加盐哈希
 *
 * @param {string} signKey - 盐值
 * @param {string} signParam - 要哈希的参数
 * @return {string} - 返回十六进制格式的哈希值
 */
function sha256WithSalt(signKey: string, signParam: string) {
  try {
    // 拼接盐值和参数
    const saltedData = signKey + signParam;

    // 计算SHA-256哈希值
    const hash = CryptoJS.SHA256(saltedData);

    // 转换为十六进制字符串并返回
    return hash.toString(CryptoJS.enc.Hex);
  } catch (error) {
    throw new Error(`SHA-256 hashing failed: ${(error as Error).message}`);
  }
}

/** 通过 RSA 方式加密传入参数来发起请求  */
export function encryptRequest(params: {
  data: Record<string, any>;
  publicKey: string;
}) {
  const { publicKey, data } = params;

  const dataSortedJsonStr = JSON.stringify(data, Object.keys(data).sort());

  /** x-nonce */
  const randomSignKey = atob(generateRandomKey());

  /** x-timestamp */
  const time = Date.now();

  const signKey = `${time}${randomSignKey}`;
  const signParam = `${dataSortedJsonStr}&${time}`;
  /** x-signature */
  const xSign = sha256WithSalt(signKey, signParam);

  /** 对 string 过后的 data 进行加密处理; ivBase64 - x-iv */
  const { encryptedData, encryptedAesKey, iv } = hybridEncrypt(
    publicKey,
    dataSortedJsonStr
  );

  return {
    ivBase64: iv,
    xSign,
    time,
    randomSignKey,
    encryptionRandomKey: encryptedAesKey,
    encryptionBody: encryptedData,
  };
}
